import { jsxs, jsx } from "react/jsx-runtime";
import { memo, useRef, useMemo, useCallback, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import { useTranslation } from "react-i18next";
import "../shared/quiz-style-context.js";
import { SubmitButton } from "../shared/submit-button.js";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import "../../../context/DevToolsContext.js";
import { QuizHeader } from "../shared/quiz-header.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { QUIZ_INLINE_STYLES } from "../shared/quiz-styles.js";
import { formatProblem } from "../../../utils/quiz-parser.js";
import { parseSelectTextOptionModels, useSelectTextQuiz } from "../hooks/use-select-text-quiz.js";
import ChevronDown from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/chevron-down.js";
import { AnimatePresence } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/components/AnimatePresence/index.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
import Check from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/check.js";
function PortalDropdown({ isOpen, anchorRef, options, selectedOptionIdx, onSelect, onClose }) {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  useLayoutEffect(() => {
    if (!isOpen || !anchorRef.current) return;
    const updatePosition = () => {
      var _a, _b;
      const rect = (_a = anchorRef.current) == null ? void 0 : _a.getBoundingClientRect();
      if (!rect) return;
      const dropdownHeight = ((_b = dropdownRef.current) == null ? void 0 : _b.offsetHeight) || 200;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const showAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      setPosition({
        top: showAbove ? rect.top + window.scrollY - dropdownHeight - 4 : rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX
      });
    };
    updatePosition();
    const handleScrollOrResize = () => onClose();
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen, anchorRef, onClose]);
  if (!isOpen) return null;
  return createPortal(
    /* @__PURE__ */ jsx(AnimatePresence, { children: /* @__PURE__ */ jsx(
      motion.div,
      {
        ref: dropdownRef,
        initial: { opacity: 0, y: -10, scale: 0.95 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: -10, scale: 0.95 },
        style: { position: "absolute", top: position.top, left: position.left },
        className: "z-9999 bg-white rounded-xl border border-gray-200 shadow-xl min-w-32",
        onClick: (e) => e.stopPropagation(),
        children: options.map((option) => {
          if (!option.isSelectable) return null;
          return /* @__PURE__ */ jsxs(
            "button",
            {
              onClick: () => onSelect(option.rawIndex),
              className: cn(
                "w-full px-4 py-3 text-left text-lg font-medium transition-colors flex items-center gap-2",
                "hover:bg-blue-50",
                selectedOptionIdx === option.rawIndex && "bg-blue-100 text-blue-700"
              ),
              children: [
                selectedOptionIdx === option.rawIndex && /* @__PURE__ */ jsx(Check, { className: "w-4 h-4 text-blue-600" }),
                /* @__PURE__ */ jsx(KatexText, { text: option.label })
              ]
            },
            option.rawIndex
          );
        })
      }
    ) }),
    document.body
  );
}
const SelectBlank = memo(function SelectBlank2({
  quizId,
  index,
  content,
  selectedOptionIdx,
  isActive,
  isOpen,
  isSolved,
  isViewSolved,
  isCorrectAnswer,
  shouldShowCorrectAnswers,
  correctAnswerIndex,
  onToggle,
  onSelect
}) {
  var _a, _b, _c;
  const buttonRef = useRef(null);
  const { t } = useTranslation("quiz");
  const options = useMemo(() => {
    return parseSelectTextOptionModels(content);
  }, [content]);
  const selectedOption = selectedOptionIdx !== null && ((_a = options[selectedOptionIdx]) == null ? void 0 : _a.isSelectable) ? options[selectedOptionIdx] : null;
  const showAsCorrect = isSolved && isCorrectAnswer;
  const showAsWrong = isSolved && selectedOptionIdx !== null && !isCorrectAnswer && !shouldShowCorrectAnswers;
  const correctAnswer = ((_b = options[correctAnswerIndex ?? 0]) == null ? void 0 : _b.label) ?? ((_c = options.find((option) => option.isSelectable)) == null ? void 0 : _c.label) ?? "";
  const handleSelect = useCallback((optIdx) => {
    onSelect(index, optIdx);
  }, [index, onSelect]);
  const handleClose = useCallback(() => {
    onToggle(index);
  }, [index, onToggle]);
  return /* @__PURE__ */ jsxs("span", { className: "inline-block my-1", children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        ref: buttonRef,
        onClick: (e) => {
          e.stopPropagation();
          onToggle(index);
        },
        disabled: isSolved || isViewSolved,
        className: cn(
          QUIZ_INLINE_STYLES.base,
          QUIZ_INLINE_STYLES.select,
          "gap-1 border",
          !isSolved && !isActive && !selectedOption && "border-gray-300 text-gray-400",
          isActive && !selectedOption && "border-blue-500 ring-2 ring-blue-100",
          !isSolved && selectedOption && "border-blue-500 bg-blue-50 text-blue-700",
          showAsCorrect && QUIZ_INLINE_STYLES.correct,
          showAsWrong && QUIZ_INLINE_STYLES.wrong,
          shouldShowCorrectAnswers && isSolved && !isCorrectAnswer && "border-green-500 bg-green-50 text-green-700",
          (isSolved || isViewSolved) && "cursor-not-allowed"
        ),
        children: [
          /* @__PURE__ */ jsx("span", { children: shouldShowCorrectAnswers && isSolved && !isCorrectAnswer ? /* @__PURE__ */ jsx(KatexText, { text: correctAnswer }) : selectedOption ? /* @__PURE__ */ jsx(KatexText, { text: selectedOption.label }) : t("types.selectText.placeholder") }),
          !isSolved && !isViewSolved && /* @__PURE__ */ jsx(ChevronDown, { className: cn(
            "w-4 h-4 transition-transform",
            isOpen && "rotate-180"
          ) })
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      PortalDropdown,
      {
        isOpen: isOpen && !isSolved && !isViewSolved,
        anchorRef: buttonRef,
        options,
        selectedOptionIdx,
        onSelect: handleSelect,
        onClose: handleClose
      }
    )
  ] }, `select-${quizId}-${index}`);
});
function SelectTextQuizAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false
}) {
  const { t } = useTranslation("quiz");
  const {
    answers,
    currentIndex,
    openDropdown,
    selectCount,
    problemKey,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleToggleDropdown,
    handleSelect,
    handleSubmit
  } = useSelectTextQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  const renderedProblem = useMemo(() => {
    if (answers.length === 0 && selectCount > 0) return [];
    return formatProblem(
      quiz.problem,
      `${quizId}-problem-${problemKey}`,
      (index, content) => {
        var _a, _b;
        const selectedOptionIdx = answers[index] !== "" ? parseInt(answers[index]) : null;
        const isActive = currentIndex === index && !isSolved && !isViewSolved;
        const isOpen = openDropdown === index;
        const isCorrectByCorrects = (_a = solveResult == null ? void 0 : solveResult.corrects) == null ? void 0 : _a[index];
        const isCorrectAnswer = isCorrectByCorrects !== void 0 && isCorrectByCorrects !== null ? isCorrectByCorrects : selectedOptionIdx === 0;
        const correctAnswerFromServer = (_b = solveResult == null ? void 0 : solveResult.correctAnswers) == null ? void 0 : _b[index];
        const correctAnswerIndex = typeof correctAnswerFromServer === "number" ? correctAnswerFromServer : typeof correctAnswerFromServer === "string" ? parseInt(correctAnswerFromServer) : null;
        return /* @__PURE__ */ jsx(
          SelectBlank,
          {
            quizId,
            index,
            content,
            selectedOptionIdx,
            isActive,
            isOpen,
            isSolved,
            isViewSolved,
            isCorrectAnswer,
            shouldShowCorrectAnswers,
            correctAnswerIndex,
            onToggle: handleToggleDropdown,
            onSelect: handleSelect
          },
          `select-${quizId}-${problemKey}-${index}`
        );
      },
      quiz.additionalImages ?? void 0
    );
  }, [answers, currentIndex, openDropdown, isSolved, shouldShowCorrectAnswers, quiz.problem, quizId, isViewSolved, selectCount, quiz.additionalImages, solveResult == null ? void 0 : solveResult.corrects, solveResult == null ? void 0 : solveResult.correctAnswers, problemKey, handleToggleDropdown, handleSelect]);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsx(
      QuizHeader,
      {
        quizId,
        typeLabel: t("types.selectText.label"),
        title: quiz.title,
        difficultyLevel,
        image: quiz.image,
        imageMobile: quiz.imageMobile,
        desmos: quiz.desmos,
        vars: quiz.vars
      }
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        className: cn(
          "p-4 bg-gray-100 rounded-xl",
          "text-xl leading-relaxed text-gray-700"
        ),
        children: renderedProblem
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? false,
        show: isSolved,
        disableSound,
        isViewSolved
      }
    ),
    isSolved && (solveResult == null ? void 0 : solveResult.description) && !hideExplanation && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: solveResult.description,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: !solveResult.correct,
        actionLabel: isShowingCorrectAnswers ? t("common.viewMyAnswer") : t("common.viewCorrectAnswer"),
        onActionClick: () => setIsShowingCorrectAnswers((v) => !v)
      }
    )
  ] });
}
export {
  SelectTextQuizAdult
};
//# sourceMappingURL=select-text-adult.js.map
